import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Send, Phone, Mail, MapPin, CheckCircle2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal, Overline } from "../components/Reveal";
import { API, BOOKING_TYPES, PHONE, PHONE_TEL, EMAIL } from "../data";

const EMPTY = { name: "", email: "", phone: "", address: "", lighting_type: "", preferred_date: "", comments: "" };

const inputCls =
  "w-full rounded-xl bg-obsidian/70 border border-white/10 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-gold/60 focus:ring-1 focus:ring-gold/40 transition-colors";
const labelCls = "block text-xs font-mono uppercase tracking-[0.2em] text-slate-400 mb-2";

export default function BookNow() {
  const [params] = useSearchParams();
  const preset = params.get("type");
  const [form, setForm] = useState({
    ...EMPTY,
    lighting_type: preset && BOOKING_TYPES.includes(preset) ? preset : "",
  });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.address.trim() || !form.lighting_type) {
      toast.error("Please fill in your name, property address, and lighting type.");
      return;
    }
    if (!form.email.trim() && !form.phone.trim()) {
      toast.error("Please add an email or phone number so we can reach you.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/bookings`, form);
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      toast.error("Something went wrong sending your request. Please call or text us instead.");
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <div data-testid="book-page" className="pt-20 min-h-screen flex items-center">
        <div className="max-w-2xl mx-auto px-4 sm:px-8 py-24 text-center">
          <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }}>
            <CheckCircle2 className="h-16 w-16 text-gold mx-auto mb-6" />
            <h1 data-testid="booking-success-message" className="text-3xl sm:text-4xl font-serif font-bold text-slate-100 mb-4">
              You're on the books, {form.name.split(" ")[0]}!
            </h1>
            <p className="text-slate-400 leading-relaxed mb-4">
              Your {form.lighting_type.toLowerCase()} request for <span className="text-slate-200">{form.address}</span> has been sent straight to our inbox.
            </p>
            <p className="text-slate-400 leading-relaxed mb-10">
              We'll reach out shortly to schedule your free quote. Need us sooner? Call or text{" "}
              <a href={PHONE_TEL} className="text-gold hover:text-gold-light font-semibold">{PHONE}</a>.
            </p>
            <button
              data-testid="booking-new-request-button"
              onClick={() => { setDone(false); setForm(EMPTY); }}
              className="px-8 py-3.5 rounded-full font-semibold text-silver-light border border-silver/30 glass-panel hover:border-gold/60 hover:text-gold-light transition-all duration-300"
            >
              Submit Another Request
            </button>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="book-page" className="pt-20">
      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 pt-16 pb-24">
        <Reveal>
          <Overline>Book now</Overline>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold text-slate-100 tracking-tight leading-[1.1] mb-4">
            Let's light up <span className="text-gold-gradient">your place</span>
          </h1>
          <p className="max-w-2xl text-slate-400 text-base sm:text-lg leading-relaxed">
            Tell us what you're dreaming of. We'll come measure, design, and quote it — free. Booking holiday lights? Lock in before November 1st for 50% off installation.
          </p>
        </Reveal>

        <div className="mt-14 grid gap-10 lg:grid-cols-3">
          <Reveal className="lg:col-span-2">
            <form onSubmit={submit} data-testid="booking-form" className="rounded-3xl border border-white/5 bg-card2/50 p-6 sm:p-10">
              <div className="grid gap-5 sm:grid-cols-2 mb-5">
                <div>
                  <label htmlFor="book-name" className={labelCls}>Full name *</label>
                  <input id="book-name" data-testid="book-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Jordan Smith" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="book-phone" className={labelCls}>Phone</label>
                  <input id="book-phone" data-testid="book-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="(801) 555-0123" className={inputCls} />
                </div>
              </div>

              <div className="mb-5">
                <label htmlFor="book-email" className={labelCls}>Email</label>
                <input id="book-email" type="email" data-testid="book-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@email.com" className={inputCls} />
              </div>

              <div className="mb-6">
                <label htmlFor="book-address" className={labelCls}>Property address *</label>
                <input id="book-address" data-testid="book-address-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="1234 S Main St, Draper, UT 84020" className={inputCls} />
              </div>

              <div className="mb-6">
                <label className={labelCls}>Which type of lighting do you need? *</label>
                <div data-testid="book-type-picker" className="flex flex-wrap gap-2.5">
                  {BOOKING_TYPES.map((t) => (
                    <button
                      type="button"
                      key={t}
                      data-testid={`book-type-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      onClick={() => setForm({ ...form, lighting_type: t })}
                      className={`px-4 py-2.5 rounded-full text-sm font-medium border transition-all duration-200 ${
                        form.lighting_type === t
                          ? "border-gold text-obsidian bg-gradient-to-br from-gold-light via-gold to-gold-dark font-semibold"
                          : "border-white/10 text-slate-300 hover:border-gold/40 hover:-translate-y-0.5"
                      }`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mb-6">
                <label htmlFor="book-date" className={labelCls}>Preferred install date (optional)</label>
                <input id="book-date" type="date" data-testid="book-date-input" value={form.preferred_date} onChange={(e) => setForm({ ...form, preferred_date: e.target.value })} className={`${inputCls} [color-scheme:dark]`} />
              </div>

              <div className="mb-8">
                <label htmlFor="book-comments" className={labelCls}>Comments & ideas</label>
                <textarea
                  id="book-comments"
                  data-testid="book-comments-input"
                  rows={4}
                  value={form.comments}
                  onChange={(e) => setForm({ ...form, comments: e.target.value })}
                  placeholder="Tell us about your home, colors you love, a competitor quote you want beaten, or your own wild idea..."
                  className={`${inputCls} resize-none`}
                />
              </div>

              <button
                type="submit"
                data-testid="booking-submit-button"
                disabled={sending}
                className="w-full sm:w-auto inline-flex justify-center items-center gap-2 px-10 py-4 rounded-full font-semibold text-obsidian bg-gradient-to-br from-gold-light via-gold to-gold-dark transition-all duration-300 hover:shadow-[0_0_35px_rgba(233,199,103,0.5)] hover:-translate-y-0.5 disabled:opacity-60 disabled:translate-y-0"
              >
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                {sending ? "Sending..." : "Request My Free Quote"}
              </button>
            </form>
          </Reveal>

          <Reveal delay={0.1}>
            <aside className="space-y-6">
              <div className="rounded-3xl border border-gold/20 bg-card2/50 p-8">
                <h3 className="text-xs font-mono uppercase tracking-[0.25em] text-gold mb-6">Prefer to talk?</h3>
                <div className="space-y-5">
                  <a href={PHONE_TEL} data-testid="book-phone-link" className="flex items-center gap-3 text-slate-200 hover:text-gold-light transition-colors">
                    <Phone className="h-5 w-5 text-gold shrink-0" />
                    <span className="font-serif text-xl">{PHONE}</span>
                  </a>
                  <a href={`mailto:${EMAIL}`} data-testid="book-email-link" className="flex items-center gap-3 text-slate-300 hover:text-gold-light transition-colors text-sm break-all">
                    <Mail className="h-5 w-5 text-gold shrink-0" />
                    {EMAIL}
                  </a>
                  <div className="flex items-start gap-3 text-sm text-slate-400">
                    <MapPin className="h-5 w-5 text-gold shrink-0 mt-0.5" />
                    <span>Serving all of Salt Lake County. Utah, Davis & Weber counties for a small travel fee.</span>
                  </div>
                </div>
              </div>
              <div className="rounded-3xl border border-white/5 bg-gradient-to-br from-gold/10 to-transparent p-8">
                <p className="text-xs font-mono uppercase tracking-[0.25em] text-gold mb-3">Holiday special</p>
                <p className="font-serif text-2xl text-slate-100 mb-2">50% off installation</p>
                <p className="text-sm text-slate-400 leading-relaxed">Book holiday lighting before November 1st. Custom cut, commercial grade, free takedown after the season.</p>
              </div>
            </aside>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
